import { baseApi } from "@/api/baseApi";
import { CONTRACT_FAMILIES_TAGS } from "@/api/contract-families.constraints";
import {
  ActivePipelineRunsResponse,
  AdminBulkPipelineRunCreatePayload,
  AutomatedRunStatus,
  ContractFamilyStagedValueOverridePayload,
  FailedSnapshotsExcludePayload,
  FamilyCollectionPage,
  PipelineConfigurationResponse,
  PipelineConfigurationOptions,
  PipelineConfigurationUpdatePayload,
  PipelineRunCreatePayload,
  PipelineRunResponse,
  PipelineReviewPage,
  PipelineKeyTerms,
  PipelineCitationTarget,
  PipelineSourceLink,
  PublishResponse,
  StagedValue,
  PipelineSummaryJobResponse,
  PipelineScope,
  PipelineScopeSelectionPayload,
  SummaryPromptsPayload,
  SummaryColumnRerunPayload,
  SummaryColumnRerunResponse,
  SupplierFolderContractSummaryRegeneratePayload,
  CollectionSummariesCreatePayload,
  SupplierFolderPipelineRunCreatePayload,
} from "@/types/contract-family-pipeline";
import { SUPPLIER_TAGS } from "@/api/suppliers.constraints";
import {
  patchPipelineStagedValue,
  patchPipelineSummaryGridCellDetail,
} from "@/modules/snapshots/grid/pipelinePreviewCache";
import type {
  PipelineSummaryGridCellDetail,
  PipelineSummaryGridQueryPayload,
  PipelineSummaryGridResponse,
} from "@/modules/snapshots/grid/pipelinePreview.types";

const BASE_URL = "/pf-mainweb/document/contract-families";

export const pipelineRequests = {
  scope: (data: PipelineScopeSelectionPayload) => ({
    url: `${BASE_URL}/pipeline-runs/scope-preview`,
    method: "POST" as const,
    data,
  }),
  create: (data: PipelineRunCreatePayload) => ({
    url: `${BASE_URL}/pipeline-runs`,
    method: "POST" as const,
    data,
  }),
  createSupplierFolder: (data: SupplierFolderPipelineRunCreatePayload) => ({
    url: `${BASE_URL}/pipeline-runs/supplier-folder`,
    method: "POST" as const,
    data,
  }),
  regenerateSupplierFolder: (data: SupplierFolderContractSummaryRegeneratePayload) => ({
    url: `${BASE_URL}/pipeline-runs/supplier-folder/regenerate`,
    method: "POST" as const,
    data,
  }),
  createCollectionSummaries: (data: CollectionSummariesCreatePayload) => ({
    url: `${BASE_URL}/pipeline-runs/collection-summaries`,
    method: "POST" as const,
    data,
  }),
  latestCollectionSummaries: (snapshotCollectionId: string) => ({
    url: `${BASE_URL}/pipeline-runs/collection-summaries/latest`,
    method: "GET" as const,
    params: { snapshot_collection_id: snapshotCollectionId },
  }),
  cancel: (runId: string) => ({
    url: `${BASE_URL}/pipeline-runs/${encodeURIComponent(runId)}/cancel`,
    method: "POST" as const,
  }),
  createAdminBulk: (data: AdminBulkPipelineRunCreatePayload) => ({
    url: `${BASE_URL}/pipeline-runs/admin-bulk`,
    method: "POST" as const,
    data,
  }),
  retry: (runId: string) => ({
    url: `${BASE_URL}/pipeline-runs/${runId}/retry-snapshots`,
    method: "POST" as const,
  }),
  exclude: (runId: string, data: FailedSnapshotsExcludePayload) => ({
    url: `${BASE_URL}/pipeline-runs/${runId}/exclude-failed-snapshots`,
    method: "POST" as const,
    data,
  }),
  categorise: (runId: string) => ({
    url: `${BASE_URL}/pipeline-runs/${runId}/start-categorisation`,
    method: "POST" as const,
  }),
  configuration: () => ({
    url: `${BASE_URL}/pipeline-configuration`,
    method: "GET" as const,
  }),
  configurationOptions: (templateId?: string) => ({
    url: `${BASE_URL}/pipeline-configuration/options`,
    method: "GET" as const,
    params: { template_id: templateId },
  }),
  updateConfiguration: (data: PipelineConfigurationUpdatePayload) => ({
    url: `${BASE_URL}/pipeline-configuration`,
    method: "PUT" as const,
    data,
  }),
};

/** Everything that shows where an automated contract summary run stands. */
const AUTOMATED_RUN_STATE_TAGS = [
  { type: SUPPLIER_TAGS.SUPPLIER_FAMILIES },
  { type: CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_AUTOMATED_RUN_TAG },
];

export const snapshotCollectionRoute = (templateId: string, collectionId: string) =>
  `/snapshots/grid/${encodeURIComponent(templateId)}/${encodeURIComponent(collectionId)}/`;

export const contractFamilyPipelineApi = baseApi.injectEndpoints({
  overrideExisting: false,
  endpoints: builder => ({
    getActiveContractFamilyPipelineRuns: builder.query<ActivePipelineRunsResponse, string | null>({
      query: folderFseId => ({
        url: `${BASE_URL}/pipeline-runs`,
        method: "GET",
        params: { folder_fse_id: folderFseId ?? undefined },
      }),
    }),
    createContractFamilyPipelineRun: builder.mutation<PipelineRunResponse, PipelineRunCreatePayload>({
      query: pipelineRequests.create,
    }),
    createSupplierFolderContractFamilyPipelineRun: builder.mutation<
      PipelineRunResponse,
      SupplierFolderPipelineRunCreatePayload
    >({
      query: pipelineRequests.createSupplierFolder,
    }),
    createAdminBulkContractFamilyPipelineRun: builder.mutation<PipelineRunResponse, AdminBulkPipelineRunCreatePayload>({
      query: pipelineRequests.createAdminBulk,
    }),
    regenerateSupplierFolderContractSummary: builder.mutation<
      PipelineRunResponse,
      SupplierFolderContractSummaryRegeneratePayload
    >({
      query: pipelineRequests.regenerateSupplierFolder,
      // The folder's summary status lives on the supplier folder reads; the run also reads the base collection.
      invalidatesTags: AUTOMATED_RUN_STATE_TAGS,
    }),
    createSupplierSummaryCollection: builder.mutation<PipelineRunResponse, CollectionSummariesCreatePayload>({
      query: pipelineRequests.createCollectionSummaries,
      // Every family folder of the collection gets a live run; their status lives on supplier folder reads.
      invalidatesTags: AUTOMATED_RUN_STATE_TAGS,
    }),
    getLatestCollectionSummariesRun: builder.query<AutomatedRunStatus | null, string>({
      query: pipelineRequests.latestCollectionSummaries,
      providesTags: [CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_AUTOMATED_RUN_TAG],
    }),
    cancelAutomatedRun: builder.mutation<AutomatedRunStatus, string>({
      query: pipelineRequests.cancel,
      invalidatesTags: (_result, _error, runId) => [
        ...AUTOMATED_RUN_STATE_TAGS,
        { type: CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_PIPELINE_RUN_TAG, id: runId },
      ],
    }),
    getContractFamilyPipelineRun: builder.query<PipelineRunResponse, string>({
      query: runId => ({ url: `${BASE_URL}/pipeline-runs/${runId}`, method: "GET" }),
      providesTags: (_result, _error, runId) => [
        { type: CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_PIPELINE_RUN_TAG, id: runId },
      ],
    }),
    getContractFamilyPipelineScope: builder.query<PipelineScope, PipelineScopeSelectionPayload>({
      query: pipelineRequests.scope,
    }),
    retryPipelineSnapshots: builder.mutation<PipelineRunResponse, string>({
      query: pipelineRequests.retry,
      invalidatesTags: (_result, _error, runId) => [
        { type: CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_PIPELINE_RUN_TAG, id: runId },
      ],
    }),
    excludeFailedPipelineSnapshots: builder.mutation<
      PipelineRunResponse,
      { runId: string; payload: FailedSnapshotsExcludePayload }
    >({
      query: ({ runId, payload }) => pipelineRequests.exclude(runId, payload),
      invalidatesTags: (_result, _error, { runId }) => [
        { type: CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_PIPELINE_RUN_TAG, id: runId },
      ],
    }),
    startPipelineCategorisation: builder.mutation<PipelineRunResponse, string>({
      query: pipelineRequests.categorise,
      invalidatesTags: (_result, _error, runId) => [
        { type: CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_PIPELINE_RUN_TAG, id: runId },
      ],
    }),
    confirmContractFamilyPipelineGrouping: builder.mutation<PipelineRunResponse, string>({
      query: runId => ({ url: `${BASE_URL}/pipeline-runs/${runId}/confirm-grouping`, method: "POST" }),
    }),
    editContractFamilyPipelineGrouping: builder.mutation<PipelineRunResponse, string>({
      query: runId => ({ url: `${BASE_URL}/pipeline-runs/${runId}/edit-grouping`, method: "POST" }),
    }),
    updateContractFamilyPipelinePrompts: builder.mutation<
      PipelineRunResponse,
      { runId: string; payload: SummaryPromptsPayload }
    >({
      query: ({ runId, payload }) => ({
        url: `${BASE_URL}/pipeline-runs/${runId}/prompts`,
        method: "PUT",
        data: payload,
      }),
    }),
    generateContractFamilyPipelineSummaries: builder.mutation<PipelineSummaryJobResponse, string>({
      query: runId => ({ url: `${BASE_URL}/pipeline-runs/${runId}/generate-summaries`, method: "POST" }),
    }),
    rerunContractFamilyPipelineSummaryColumn: builder.mutation<
      SummaryColumnRerunResponse,
      { runId: string; payload: SummaryColumnRerunPayload }
    >({
      query: ({ runId, payload }) => ({
        url: `${BASE_URL}/pipeline-runs/${runId}/rerun-summary-column`,
        method: "POST",
        data: payload,
      }),
      invalidatesTags: (_result, _error, { runId }) => [
        { type: CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_PIPELINE_RUN_TAG, id: runId },
      ],
    }),
    getCompatibleFamilyCollections: builder.query<
      FamilyCollectionPage,
      { templateId: string; cursor?: string; query?: string }
    >({
      query: ({ templateId, cursor, query }) => ({
        url: `${BASE_URL}/family-collections/compatible`,
        method: "GET",
        params: { template_id: templateId, cursor, query },
      }),
    }),
    getContractFamilyPipelineReview: builder.query<
      PipelineReviewPage,
      { runId: string; cursor?: string; query?: string }
    >({
      query: ({ runId, cursor, query }) => ({
        url: `${BASE_URL}/pipeline-runs/${runId}/review`,
        method: "GET",
        params: { cursor, query },
      }),
    }),
    excludeContractFamilyReviewDocument: builder.mutation<
      PipelineRunResponse,
      { runId: string; familyId: string; documentId: string }
    >({
      query: ({ runId, familyId, documentId }) => ({
        url: `${BASE_URL}/pipeline-runs/${runId}/families/${familyId}/documents/${documentId}/exclude`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { runId }) => [
        { type: CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_PIPELINE_RUN_TAG, id: runId },
      ],
    }),
    getContractFamilyPipelineKeyTerms: builder.query<PipelineKeyTerms, { runId: string; documentId: string }>({
      query: ({ runId, documentId }) => ({
        url: `${BASE_URL}/pipeline-runs/${runId}/documents/${documentId}/key-terms`,
        method: "GET",
      }),
    }),
    getContractFamilyPipelineSourceLink: builder.query<PipelineSourceLink, string>({
      query: runId => ({ url: `${BASE_URL}/pipeline-runs/${runId}/source-link`, method: "GET" }),
    }),
    getContractFamilySummaryGrid: builder.query<
      PipelineSummaryGridResponse,
      { runId: string; payload: PipelineSummaryGridQueryPayload }
    >({
      query: ({ runId, payload }) => ({
        url: `${BASE_URL}/pipeline-runs/${runId}/summary-grid/query`,
        method: "POST",
        data: payload,
      }),
    }),
    getContractFamilySummaryGridCell: builder.query<
      PipelineSummaryGridCellDetail,
      { runId: string; rowKind: "family_summary" | "source_document"; rowId: string; fieldId: string }
    >({
      query: ({ runId, rowKind, rowId, fieldId }) => ({
        url: `${BASE_URL}/pipeline-runs/${runId}/summary-grid/cells/${rowKind}/${rowId}/${fieldId}`,
        method: "GET",
      }),
    }),
    resolveContractFamilyPipelineCitation: builder.query<
      PipelineCitationTarget,
      { runId: string; stagedValueId: string; citationIndex: number }
    >({
      query: ({ runId, stagedValueId, citationIndex }) => ({
        url: `${BASE_URL}/pipeline-runs/${runId}/citations/${stagedValueId}/${citationIndex}`,
        method: "GET",
      }),
    }),
    overrideContractFamilyStagedValue: builder.mutation<StagedValue, ContractFamilyStagedValueOverridePayload>({
      query: ({ runId, stagedOutcomeId, fieldId, value }) => ({
        url: `${BASE_URL}/pipeline-runs/${runId}/staged-outcomes/${stagedOutcomeId}/values/${fieldId}`,
        method: "PATCH",
        data: { value },
      }),
      async onQueryStarted({ runId, stagedOutcomeId, fieldId, summaryRowId }, { dispatch, getState, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const args = contractFamilyPipelineApi.util
            .selectCachedArgsForQuery(getState(), "getContractFamilySummaryGrid")
            .filter(arg => arg.runId === runId);
          for (const arg of args) {
            dispatch(
              contractFamilyPipelineApi.util.updateQueryData("getContractFamilySummaryGrid", arg, draft =>
                patchPipelineStagedValue(draft, stagedOutcomeId, fieldId, data),
              ),
            );
          }
          if (summaryRowId) {
            const detailArgs = contractFamilyPipelineApi.util
              .selectCachedArgsForQuery(getState(), "getContractFamilySummaryGridCell")
              .filter(
                arg =>
                  arg.runId === runId &&
                  arg.rowKind === "family_summary" &&
                  arg.rowId === summaryRowId &&
                  arg.fieldId === fieldId,
              );
            for (const arg of detailArgs) {
              dispatch(
                contractFamilyPipelineApi.util.updateQueryData("getContractFamilySummaryGridCell", arg, draft =>
                  patchPipelineSummaryGridCellDetail(draft, fieldId, data),
                ),
              );
            }
          }
        } catch {
          return;
        }
      },
    }),
    publishContractFamilyPipeline: builder.mutation<PublishResponse, { runId: string; idempotencyKey: string }>({
      query: ({ runId, idempotencyKey }) => ({
        url: `${BASE_URL}/pipeline-runs/${runId}/publish`,
        method: "POST",
        data: { idempotency_key: idempotencyKey },
      }),
      invalidatesTags: (_result, _error, { runId }) => [
        { type: CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_PIPELINE_RUN_TAG, id: runId },
      ],
    }),
    getContractFamilyPipelineConfiguration: builder.query<PipelineConfigurationResponse, void>({
      query: pipelineRequests.configuration,
      providesTags: [CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_PIPELINE_CONFIGURATION_TAG],
    }),
    getContractFamilyPipelineConfigurationOptions: builder.query<PipelineConfigurationOptions, string | undefined>({
      query: templateId => ({
        ...pipelineRequests.configurationOptions(templateId),
      }),
    }),
    updateContractFamilyPipelineConfiguration: builder.mutation<
      PipelineConfigurationResponse,
      PipelineConfigurationUpdatePayload
    >({
      query: pipelineRequests.updateConfiguration,
      invalidatesTags: [CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_PIPELINE_CONFIGURATION_TAG],
    }),
  }),
});

export const {
  useGetActiveContractFamilyPipelineRunsQuery,
  useCreateContractFamilyPipelineRunMutation,
  useCreateSupplierFolderContractFamilyPipelineRunMutation,
  useCreateAdminBulkContractFamilyPipelineRunMutation,
  useRegenerateSupplierFolderContractSummaryMutation,
  useCreateSupplierSummaryCollectionMutation,
  useGetLatestCollectionSummariesRunQuery,
  useCancelAutomatedRunMutation,
  useGetContractFamilyPipelineRunQuery,
  useGetContractFamilyPipelineScopeQuery,
  useRetryPipelineSnapshotsMutation,
  useExcludeFailedPipelineSnapshotsMutation,
  useStartPipelineCategorisationMutation,
  useConfirmContractFamilyPipelineGroupingMutation,
  useEditContractFamilyPipelineGroupingMutation,
  useUpdateContractFamilyPipelinePromptsMutation,
  useGenerateContractFamilyPipelineSummariesMutation,
  useRerunContractFamilyPipelineSummaryColumnMutation,
  useGetContractFamilyPipelineConfigurationQuery,
  useGetContractFamilyPipelineConfigurationOptionsQuery,
  useUpdateContractFamilyPipelineConfigurationMutation,
  useGetCompatibleFamilyCollectionsQuery,
  useGetContractFamilyPipelineReviewQuery,
  useExcludeContractFamilyReviewDocumentMutation,
  useGetContractFamilyPipelineKeyTermsQuery,
  useGetContractFamilyPipelineSourceLinkQuery,
  useGetContractFamilySummaryGridQuery,
  useLazyGetContractFamilySummaryGridCellQuery,
  useLazyResolveContractFamilyPipelineCitationQuery,
  useOverrideContractFamilyStagedValueMutation,
  usePublishContractFamilyPipelineMutation,
} = contractFamilyPipelineApi;
